import { useEffect, useMemo, useState } from "react";
import { Loader2, Save, Percent, IndianRupee } from "lucide-react";
import { api, inr } from "../../lib/api";
import { Field } from "./AdminRestaurants";
import { toast } from "sonner";
import AdminShopPicker from "../../components/AdminShopPicker";

// Local defaults that mirror backend `_PRICING_DEFAULTS` in shops.py.
// Used as a fallback when a shop's pricing GET fails (e.g. soft-deleted
// in another tab) so the page stays usable instead of throwing.
const PRICING_DEFAULTS = {
    sp_increment_type: "PERCENT", sp_increment_value: 0,
    mrp_increment_type: "PERCENT", mrp_increment_value: 0,
    commission_type: "PERCENT", commission_value: 18,
    gst_enabled: false, gst_percent: 5,
};

export default function AdminPricing() {
    const [rests, setRests] = useState([]);
    const [owners, setOwners] = useState([]);
    const [rid, setRid] = useState("");
    const [cfg, setCfg] = useState(null);
    const [tenantCfg, setTenantCfg] = useState({ gst_on_delivery_enabled: false, gst_on_delivery_percent: 5 });
    const [charges, setCharges] = useState([]);
    const [overrides, setOverrides] = useState({});
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/admin/restaurants")
            .then(({ data }) => {
                // iter62b — sort alphabetically by name so the picker is
                // browseable (the API returns shops in creation order
                // which is useless once the tenant has 20+ shops).
                const sorted = [...(data || [])].sort((a, b) =>
                    (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }),
                );
                setRests(sorted);
                if (sorted?.[0]) setRid(sorted[0].id);
            })
            .catch(() => toast.error("Failed to load shops"));
        // Load owners so the search picker can match by owner phone too.
        api.get("/admin/owners")
            .then(({ data }) => setOwners(data || []))
            .catch(() => setOwners([]));
        api.get("/admin/tenant-settings")
            .then(({ data }) => setTenantCfg(data))
            .catch(() => { /* defaults already set */ });
        api.get("/admin/additional-charges")
            .then(({ data }) => setCharges(data))
            .catch(() => setCharges([]));
    }, []);

    // iter84 — owners are loaded so the shared <AdminShopPicker> can match
    // by owner phone/name. We pass the raw list via ``ownersOverride``;
    // the picker builds its own ownersByShopId map internally.
    // (Pre-iter84 we built that map here and passed it to an inline
    // ShopPicker; the inline picker has been promoted to a shared component.)

    useEffect(() => {
        if (!rid) return;
        api.get(`/admin/restaurants/${rid}/pricing`)
            .then(({ data }) => setCfg(data))
            .catch((e) => {
                if (e?.response?.status === 404) {
                    // Shop was soft-deleted in another tab — fall back to defaults so the page
                    // doesn't 404-crash and the operator can pick another shop.
                    setCfg({ ...PRICING_DEFAULTS });
                    toast.error("Selected shop is no longer available. Please pick another.");
                } else {
                    toast.error("Failed to load pricing");
                }
            });
        api.get(`/admin/restaurants/${rid}/additional-charge-overrides`)
            .then(({ data }) => setOverrides(data || {}))
            .catch(() => setOverrides({}));
    }, [rid]);

    const saveTenant = async () => {
        try {
            await api.patch("/admin/tenant-settings", {
                gst_on_delivery_enabled: !!tenantCfg.gst_on_delivery_enabled,
                gst_on_delivery_percent: Number(tenantCfg.gst_on_delivery_percent || 0),
            });
            toast.success("Tenant GST settings saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to save tenant settings");
        }
    };

    const save = async () => {
        if (!rid || !cfg) return;
        setBusy(true);
        try {
            await api.patch(`/admin/restaurants/${rid}/pricing`, {
                sp_increment_type: cfg.sp_increment_type,
                sp_increment_value: Number(cfg.sp_increment_value),
                mrp_increment_type: cfg.mrp_increment_type,
                mrp_increment_value: Number(cfg.mrp_increment_value),
                commission_type: cfg.commission_type,
                commission_value: Number(cfg.commission_value),
                gst_enabled: !!cfg.gst_enabled,
                gst_percent: Number(cfg.gst_percent),
            });
            await api.put(`/admin/restaurants/${rid}/additional-charge-overrides`, overrides);
            toast.success("Pricing & charges saved");
        } catch (e) {
            // Surface the real error so the operator can act on it.
            const status = e?.response?.status;
            const msg = e?.response?.data?.detail
                || (status === 404 ? "Shop not found — refresh and try again"
                    : status === 403 ? "You don't have permission to edit pricing"
                        : "Failed to save");
            toast.error(msg);
        } finally { setBusy(false); }
    };

    // Preview using ₹200 menu_price
    const preview = (() => {
        if (!cfg) return null;
        const menu = 200;
        const round4 = (x) => Math.round(x * 10000) / 10000;
        const sp = cfg.sp_increment_type === "FLAT"
            ? menu + Number(cfg.sp_increment_value || 0)
            : menu * (1 + Number(cfg.sp_increment_value || 0) / 100);
        const mrp = cfg.mrp_increment_type === "FLAT"
            ? menu + Number(cfg.mrp_increment_value || 0)
            : menu * (1 + Number(cfg.mrp_increment_value || 0) / 100);
        const comm = cfg.commission_type === "FLAT"
            ? Number(cfg.commission_value || 0)
            : menu * Number(cfg.commission_value || 0) / 100;
        const selling = Math.ceil(round4(sp));
        const finalMrp = Math.max(Math.ceil(round4(mrp)), selling);
        const payout = Math.max(0, Math.floor(round4(menu - comm)));
        const admin = selling - payout;
        const gst = cfg.gst_enabled ? Math.round(selling * Number(cfg.gst_percent || 0) / 100) : 0;
        return { selling, mrp: finalMrp, payout, admin, gst, total: selling + gst };
    })();

    if (!cfg) {
        return <div className="text-sm text-muted-foreground">Loading…</div>;
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold">Pricing & Commission</h1>
                    <p className="text-sm text-muted-foreground mt-1">Defaults applied to all products of this merchant. Override per product if needed.</p>
                </div>
                <AdminShopPicker
                    testIdPrefix="pricing-rest"
                    value={rid}
                    onChange={setRid}
                    shopsOverride={rests}
                    ownersOverride={owners}
                />
            </div>

            {/* Tenant-level GST on Delivery */}
            <div className="bg-card border border-border rounded-2xl p-5">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="font-bold text-sm">GST on Delivery (tenant default)</p>
                        <p className="text-xs text-muted-foreground">Applied to the delivery fee. Override per zone if needed.</p>
                    </div>
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input data-testid="del-gst-toggle" type="checkbox" className="w-5 h-5 accent-primary"
                            checked={!!tenantCfg.gst_on_delivery_enabled}
                            onChange={(e) => setTenantCfg({ ...tenantCfg, gst_on_delivery_enabled: e.target.checked })} />
                        <span className="text-sm font-semibold">{tenantCfg.gst_on_delivery_enabled ? "ON" : "OFF"}</span>
                    </label>
                </div>
                {tenantCfg.gst_on_delivery_enabled && (
                    <div className="mt-3 flex items-center gap-2">
                        <Field label="GST %"><input data-testid="del-gst-pct" type="number" className="inp" value={tenantCfg.gst_on_delivery_percent}
                            onChange={(e) => setTenantCfg({ ...tenantCfg, gst_on_delivery_percent: e.target.value })} /></Field>
                        <button onClick={saveTenant} className="bg-foreground text-background rounded-xl px-3 py-2 text-sm font-semibold self-end">Save</button>
                    </div>
                )}
            </div>

            <div className="grid lg:grid-cols-[1fr_360px] gap-6">
                <div className="bg-card border border-border rounded-2xl p-5 space-y-5">
                    <RuleRow
                        label="Selling Price Increment"
                        hint="Added on top of Menu Price. Customer pays this."
                        kind={cfg.sp_increment_type} value={cfg.sp_increment_value}
                        onKind={(v) => setCfg({ ...cfg, sp_increment_type: v })}
                        onValue={(v) => setCfg({ ...cfg, sp_increment_value: v })}
                        testid="sp"
                    />
                    <RuleRow
                        label="MRP Increment"
                        hint="Struck-through list price shown to customers."
                        kind={cfg.mrp_increment_type} value={cfg.mrp_increment_value}
                        onKind={(v) => setCfg({ ...cfg, mrp_increment_type: v })}
                        onValue={(v) => setCfg({ ...cfg, mrp_increment_value: v })}
                        testid="mrp"
                    />
                    <RuleRow
                        label="Business Commission"
                        hint="Deducted from Menu Price for merchant payout."
                        kind={cfg.commission_type} value={cfg.commission_value}
                        onKind={(v) => setCfg({ ...cfg, commission_type: v })}
                        onValue={(v) => setCfg({ ...cfg, commission_value: v })}
                        testid="comm"
                    />

                    <div className="border-t border-border pt-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="font-bold text-sm">GST</p>
                                <p className="text-xs text-muted-foreground">Applied to the customer bill as a separate line item.</p>
                            </div>
                            <label className="inline-flex items-center gap-2 cursor-pointer">
                                <input data-testid="gst-toggle" type="checkbox" className="w-5 h-5 accent-primary"
                                    checked={!!cfg.gst_enabled}
                                    onChange={(e) => setCfg({ ...cfg, gst_enabled: e.target.checked })} />
                                <span className="text-sm font-semibold">{cfg.gst_enabled ? "ON" : "OFF"}</span>
                            </label>
                        </div>
                        {cfg.gst_enabled && (
                            <Field label="GST %">
                                <input data-testid="gst-pct" type="number" className="inp" value={cfg.gst_percent}
                                    onChange={(e) => setCfg({ ...cfg, gst_percent: e.target.value })} />
                            </Field>
                        )}
                    </div>

                    <button data-testid="pricing-save" disabled={busy} onClick={save}
                        className="bg-primary text-primary-foreground rounded-xl px-5 py-3 font-bold inline-flex items-center gap-2 disabled:opacity-50">
                        {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save settings
                    </button>
                </div>

                {preview && (
                    <div className="bg-gradient-to-br from-primary/10 to-accent/10 border border-border rounded-2xl p-5 h-fit">
                        <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Live preview</p>
                        <p className="text-xs text-muted-foreground mt-1">For a sample Menu Price of <strong>{inr(200)}</strong></p>
                        <dl className="mt-4 space-y-2 text-sm">
                            <Row k="Menu Price (merchant)" v={inr(200)} />
                            <Row k="Customer Selling Price" v={inr(preview.selling)} strong />
                            <Row k="Customer MRP (struck)" v={inr(preview.mrp)} />
                            <Row k="Merchant Payout" v={inr(preview.payout)} pos />
                            <Row k="Admin Earn" v={inr(preview.admin)} pos />
                            {cfg.gst_enabled && (
                                <>
                                    <Row k={`+ GST ${cfg.gst_percent}%`} v={inr(preview.gst)} />
                                    <Row k="Customer Total Bill" v={inr(preview.total)} strong />
                                </>
                            )}
                        </dl>
                    </div>
                )}
            </div>

            {/* Per-restaurant additional charge overrides */}
            {charges.filter((c) => c.allow_restaurant_override).length > 0 && (
                <div className="bg-card border border-border rounded-2xl p-5">
                    <p className="font-bold text-sm">Additional charge overrides for this restaurant</p>
                    <p className="text-xs text-muted-foreground">Toggle off to skip the charge for this restaurant, or set a different amount. Charges that don&apos;t allow override aren&apos;t shown.</p>
                    <div className="mt-3 divide-y divide-border">
                        {charges.filter((c) => c.allow_restaurant_override).map((c) => {
                            const ov = overrides[c.id] || {};
                            const en = ov.enabled === undefined ? true : !!ov.enabled;
                            const amt = ov.amount === undefined || ov.amount === null ? "" : ov.amount;
                            return (
                                <div key={c.id} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`ovr-${c.id}`}>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-bold text-sm truncate">{c.name}</p>
                                        <p className="text-xs text-muted-foreground">Default ₹{c.amount} • To {c.goes_to === "ADMIN" ? "Bizil" : "Shop"}{c.taxable ? " • Taxable" : ""}</p>
                                    </div>
                                    <label className="inline-flex items-center gap-2 text-sm">
                                        <input type="checkbox" className="accent-primary w-4 h-4" data-testid={`ovr-enable-${c.id}`}
                                            checked={en}
                                            onChange={(e) => setOverrides({ ...overrides, [c.id]: { ...ov, enabled: e.target.checked } })} />
                                        <span className="font-semibold">{en ? "ON" : "OFF"}</span>
                                    </label>
                                    <input data-testid={`ovr-amount-${c.id}`} type="number" placeholder="Custom ₹" className="inp max-w-[140px]" value={amt}
                                        onChange={(e) => setOverrides({ ...overrides, [c.id]: { ...ov, amount: e.target.value === "" ? null : Number(e.target.value) } })} />
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

function RuleRow({ label, hint, kind, value, onKind, onValue, testid }) {
    return (
        <div>
            <p className="font-bold text-sm">{label}</p>
            <p className="text-xs text-muted-foreground">{hint}</p>
            <div className="mt-2 flex items-center gap-3">
                <div className="flex rounded-xl border border-border overflow-hidden">
                    <button data-testid={`${testid}-flat`} type="button" onClick={() => onKind("FLAT")}
                        className={`px-3 py-2 text-xs font-semibold inline-flex items-center gap-1 ${kind === "FLAT" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                        <IndianRupee size={12} /> Flat
                    </button>
                    <button data-testid={`${testid}-pct`} type="button" onClick={() => onKind("PERCENT")}
                        className={`px-3 py-2 text-xs font-semibold inline-flex items-center gap-1 ${kind === "PERCENT" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                        <Percent size={12} /> Percent
                    </button>
                </div>
                <input data-testid={`${testid}-val`} type="number" step="0.01" className="inp max-w-[180px]" value={value}
                    onChange={(e) => onValue(e.target.value)} />
            </div>
        </div>
    );
}

function Row({ k, v, strong, pos }) {
    return (
        <div className="flex items-center justify-between">
            <dt className="text-muted-foreground">{k}</dt>
            <dd className={`font-${strong ? "bold" : "semibold"} ${pos ? "text-emerald-600" : ""}`}>{v}</dd>
        </div>
    );
}
// iter84 — The inline ShopPicker that previously lived here has been
// promoted to ``/app/frontend/src/components/AdminShopPicker.jsx`` so
// every admin tab (Categories, Products, Inventory, Coupons,
// Settlements, Booking Settlements, Orders, Bookings, Hours, Pricing)
// can share the same searchable + alphabetical UX.
